import React, { useState, useEffect, useCallback } from 'react';
import { Container, Loader } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../lib/auth/axiosApi';
import { useSelector } from 'react-redux';
import './Ledger.css';

function LedgerPage() {
  const isLoggedIn = !!localStorage.getItem('access_token');
  const allowedCards = useSelector(state => state.permissions.allowedCards);
  const hasPermission = allowedCards.includes('ledger');

  // Data state
  const [wagers, setWagers] = useState([]);
  const [pendingForMe, setPendingForMe] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [balances, setBalances] = useState({ pairwise: [], net: [] });
  const [loading, setLoading] = useState(true);

  // Filter state
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterParticipant, setFilterParticipant] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '', description: '', category: '', amount: '',
    owed_by: '', owed_to: '', notes: '', participant_ids: [],
  });
  const [submitting, setSubmitting] = useState(false);

  // Settle Up state
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleData, setSettleData] = useState({ paid_to: '', amount: '', notes: '' });

  // Expanded row
  const [expandedId, setExpandedId] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterCategory) params.category = filterCategory;
      if (filterParticipant) params.participant = filterParticipant;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;

      const [wagersRes, pendingRes, catsRes, usersRes, balRes] = await Promise.all([
        axiosInstance.get('wagers/', { params }),
        axiosInstance.get('wagers/my_pending/'),
        axiosInstance.get('wager-categories/'),
        axiosInstance.get('wager-users/'),
        axiosInstance.get('wagers/balances/'),
      ]);

      setWagers(wagersRes.data.results || wagersRes.data);
      setPendingForMe(pendingRes.data.results || pendingRes.data);
      setCategories(catsRes.data.results || catsRes.data);
      setUsers(usersRes.data.results || usersRes.data);
      setBalances(balRes.data);
    } catch (e) {
      console.error('Failed to fetch ledger data:', e);
    }
    setLoading(false);
  }, [filterStatus, filterCategory, filterParticipant, filterDateFrom, filterDateTo]);

  useEffect(() => {
    if (isLoggedIn) fetchAll();
  }, [isLoggedIn, fetchAll]);

  // Auto-refresh so approvals from others show up without a reload. fetchAll
  // is five requests, so 10s meant 30 req/min per open tab — 30s is plenty for
  // a wager someone else has to walk over and approve.
  useEffect(() => {
    if (!isLoggedIn) return;
    const interval = setInterval(() => {
      fetchAll();
    }, 30000);
    return () => clearInterval(interval);
  }, [isLoggedIn, fetchAll]);

  const handleApprove = async (wagerId) => {
    try {
      await axiosInstance.post(`wagers/${wagerId}/approve/`);
      toast.success('Wager approved!');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to approve');
    }
  };

  const handleDeny = async (wagerId) => {
    try {
      await axiosInstance.post(`wagers/${wagerId}/deny/`);
      toast.info('Wager denied.');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to deny');
    }
  };

  const handleMarkPaid = async (wagerId) => {
    try {
      await axiosInstance.post(`wagers/${wagerId}/mark_paid/`);
      toast.success('Payment submitted — awaiting confirmation from the other party.');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to mark as paid');
    }
  };

  const handleSettleUp = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!settleData.paid_to || !settleData.amount) {
      toast.error('Select who you paid and the amount.');
      return;
    }
    if (parseFloat(settleData.amount) <= 0) {
      toast.error('Amount must be positive.');
      return;
    }
    setSubmitting(true);
    try {
      await axiosInstance.post('wagers/settle_up/', settleData);
      toast.success('Payment submitted for confirmation!');
      setSettleData({ paid_to: '', amount: '', notes: '' });
      setSettleOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to submit payment');
    }
    setSubmitting(false);
  };

  const handleSubmitWager = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!formData.title || !formData.amount || !formData.owed_by || !formData.owed_to) {
      toast.error('Please fill in all required fields.');
      return;
    }
    if (formData.owed_by === formData.owed_to) {
      toast.error('Owed by and owed to must be different people.');
      return;
    }
    if (parseFloat(formData.amount) <= 0) {
      toast.error('Amount must be positive.');
      return;
    }

    // Ensure owed_by and owed_to are in participants
    const participantSet = new Set(formData.participant_ids.map(Number));
    participantSet.add(Number(formData.owed_by));
    participantSet.add(Number(formData.owed_to));

    setSubmitting(true);
    try {
      await axiosInstance.post('wagers/', {
        ...formData,
        category: formData.category || null,
        participant_ids: Array.from(participantSet),
      });
      toast.success('Wager submitted for approval!');
      setFormData({
        title: '', description: '', category: '', amount: '',
        owed_by: '', owed_to: '', notes: '', participant_ids: [],
      });
      setFormOpen(false);
      fetchAll();
    } catch (e) {
      const errors = e.response?.data;
      if (typeof errors === 'object') {
        const msg = Object.values(errors).flat().join(' ');
        toast.error(msg || 'Failed to submit wager');
      } else {
        toast.error('Failed to submit wager');
      }
    }
    setSubmitting(false);
  };

  const clearFilters = () => {
    setFilterStatus('');
    setFilterCategory('');
    setFilterParticipant('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const toggleParticipant = (userId) => {
    const id = Number(userId);
    setFormData(prev => ({
      ...prev,
      participant_ids: prev.participant_ids.includes(id)
        ? prev.participant_ids.filter(p => p !== id)
        : [...prev.participant_ids, id],
    }));
  };

  // Get current user ID from token
  const getCurrentUserId = () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.user_id;
    } catch {
      return null;
    }
  };

  const currentUserId = getCurrentUserId();

  // Check if current user needs to approve a specific wager
  const needsMyApproval = (wager) => {
    return wager.approvals?.some(
      a => a.user === currentUserId && a.status === 'pending'
    );
  };

  // Check if there's already a pending settlement between two users
  const hasPendingSettlement = (fromUserId, toUserId) => {
    return wagers.some(w =>
      w.is_settlement && w.status === 'pending' &&
      w.owed_to === fromUserId && w.owed_by === toUserId
    );
  };

  // Separate official vs other wagers
  const officialWagers = wagers.filter(w => w.status === 'approved' || w.status === 'settled');
  const pendingWagers = wagers.filter(w => w.status === 'pending');
  const deniedWagers = wagers.filter(w => w.status === 'denied');

  if (!isLoggedIn) {
    return (
      <div className="ledger-page">
        <Container>
          <div className="ledger-header">
            <h1>Wager Ledger</h1>
            <p>Please <Link to="/login">log in</Link> to view the ledger.</p>
          </div>
        </Container>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="ledger-page">
        <Container>
          <div className="ledger-header">
            <h1>Wager Ledger</h1>
            <p>You don't have access to this feature.</p>
            <Link to="/" className="back-link">Back to Dashboard</Link>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="ledger-page">
      <Container>
        <div className="ledger-header">
          <h1>Wager Ledger</h1>
          <Link to="/" className="back-link">Back to Dashboard</Link>
        </div>

        {loading ? (
          <Loader active inline="centered" style={{ marginTop: '40px' }} />
        ) : (
          <>
            {/* Pending Approvals for Current User */}
            {pendingForMe.length > 0 && (
              <div className="ledger-section">
                <h2>Awaiting Your Approval ({pendingForMe.length})</h2>
                <table className="ledger-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Title</th>
                      <th>Amount</th>
                      <th>Who Owes</th>
                      <th>Who Is Owed</th>
                      <th>Submitted By</th>
                      <th>Approvals</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingForMe.map(w => (
                      <React.Fragment key={`pending-${w.id}`}>
                        <tr onClick={() => setExpandedId(expandedId === w.id ? null : w.id)}
                            style={{ cursor: 'pointer' }}>
                          <td>{new Date(w.created_at).toLocaleDateString()}</td>
                          <td><strong>{w.title}</strong></td>
                          <td>${parseFloat(w.amount).toFixed(2)}</td>
                          <td>{w.owed_by_username}</td>
                          <td>{w.owed_to_username}</td>
                          <td>{w.submitted_by_username}</td>
                          <td>
                            <div className="approval-list">
                              {w.approvals?.map(a => (
                                <span key={a.id} className={`approval-chip ${a.status}`}>
                                  {a.username}: {a.status}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td onClick={e => e.stopPropagation()}>
                            {needsMyApproval(w) && (
                              <>
                                <button className="action-btn approve" onClick={() => handleApprove(w.id)}>Approve</button>
                                <button className="action-btn deny" onClick={() => handleDeny(w.id)}>Deny</button>
                              </>
                            )}
                          </td>
                        </tr>
                        {expandedId === w.id && (
                          <tr className="wager-detail-row">
                            <td colSpan={8}>
                              <div className="wager-detail-content">
                                {w.description && <p><strong>Description:</strong> {w.description}</p>}
                                {w.category_name && <p><strong>Category:</strong> {w.category_name}</p>}
                                {w.notes && <p><strong>Notes:</strong> {w.notes}</p>}
                                <p><strong>Participants:</strong> {w.participants?.map(p => p.username).join(', ')}</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Submit New Wager */}
            <div className="ledger-section">
              <h2 style={{ cursor: 'pointer' }} onClick={() => setFormOpen(!formOpen)}>
                {formOpen ? 'Cancel' : 'Submit New Wager'} {formOpen ? '\u25B2' : '\u25BC'}
              </h2>
              {formOpen && (
                <form className="wager-form" onSubmit={handleSubmitWager}>
                  <div>
                    <label>Title *</label>
                    <input type="text" value={formData.title}
                      onChange={e => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g., NBA Finals Game 5" required />
                  </div>
                  <div>
                    <label>Category</label>
                    <select value={formData.category}
                      onChange={e => setFormData({ ...formData, category: e.target.value })}>
                      <option value="">Select category...</option>
                      {categories.map(c => (
                        <option key={c.id} value={c.id}>{c.display_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label>Amount ($) *</label>
                    <input type="number" step="0.01" min="0.01" value={formData.amount}
                      onChange={e => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="25.00" required />
                  </div>
                  <div>
                    <label>Who Owes *</label>
                    <select value={formData.owed_by}
                      onChange={e => setFormData({ ...formData, owed_by: e.target.value })} required>
                      <option value="">Select person...</option>
                      {users.map(u => (
                        <option key={u.id} value={u.id}>{u.username}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label>Who Is Owed *</label>
                    <select value={formData.owed_to}
                      onChange={e => setFormData({ ...formData, owed_to: e.target.value })} required>
                      <option value="">Select person...</option>
                      {users.map(u => (
                        <option key={u.id} value={u.id}>{u.username}</option>
                      ))}
                    </select>
                  </div>
                  <div className="full-width">
                    <label>Description</label>
                    <textarea value={formData.description}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Full description of the wager..." />
                  </div>
                  <div className="full-width">
                    <label>Notes / Evidence / Link</label>
                    <input type="text" value={formData.notes}
                      onChange={e => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Optional link or evidence" />
                  </div>
                  <div className="full-width">
                    <label>Additional Participants (owed by/to are auto-included)</label>
                    <div className="participant-checkboxes">
                      {users
                        .filter(u => String(u.id) !== String(formData.owed_by) && String(u.id) !== String(formData.owed_to))
                        .map(u => (
                          <label key={u.id}>
                            <input type="checkbox"
                              checked={formData.participant_ids.includes(u.id)}
                              onChange={() => toggleParticipant(u.id)} />
                            {u.username}
                          </label>
                        ))}
                    </div>
                  </div>
                  <div className="full-width">
                    <button type="submit" className="submit-btn" disabled={submitting}>
                      {submitting ? 'Submitting...' : 'Submit Wager'}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Settle Up */}
            <div className="ledger-section">
              <h2 style={{ cursor: 'pointer' }} onClick={() => setSettleOpen(!settleOpen)}>
                {settleOpen ? 'Cancel' : 'Settle Up / Record Payment'} {settleOpen ? '\u25B2' : '\u25BC'}
              </h2>
              {settleOpen && (
                <form className="wager-form" onSubmit={handleSettleUp}>
                  <div>
                    <label>I paid *</label>
                    <select value={settleData.paid_to}
                      onChange={e => setSettleData({ ...settleData, paid_to: e.target.value })} required>
                      <option value="">Select person...</option>
                      {users.filter(u => u.id !== currentUserId).map(u => (
                        <option key={u.id} value={u.id}>{u.username}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label>Amount ($) *</label>
                    <input type="number" step="0.01" min="0.01" value={settleData.amount}
                      onChange={e => setSettleData({ ...settleData, amount: e.target.value })}
                      placeholder="25.00" required />
                  </div>
                  <div className="full-width">
                    <label>Notes (optional)</label>
                    <input type="text" value={settleData.notes}
                      onChange={e => setSettleData({ ...settleData, notes: e.target.value })}
                      placeholder="e.g., Venmo, cash, etc." />
                  </div>
                  <div className="full-width">
                    <button type="submit" className="submit-btn" disabled={submitting}>
                      {submitting ? 'Submitting...' : 'Submit Payment'}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Filters */}
            <div className="ledger-section">
              <h2>Official Ledger</h2>
              <div className="filters-bar">
                <div className="filter-group">
                  <label>Status</label>
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                    <option value="">All</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="denied">Denied</option>
                    <option value="settled">Settled</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>Category</label>
                  <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
                    <option value="">All</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.display_name}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Participant</label>
                  <select value={filterParticipant} onChange={e => setFilterParticipant(e.target.value)}>
                    <option value="">All</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.username}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label>From</label>
                  <input type="date" value={filterDateFrom}
                    onChange={e => setFilterDateFrom(e.target.value)} />
                </div>
                <div className="filter-group">
                  <label>To</label>
                  <input type="date" value={filterDateTo}
                    onChange={e => setFilterDateTo(e.target.value)} />
                </div>
                <button className="clear-btn" onClick={clearFilters}>Clear</button>
              </div>

              {/* Official Ledger Table */}
              {officialWagers.length === 0 && pendingWagers.length === 0 && deniedWagers.length === 0 ? (
                <div className="empty-state">No wagers found. Submit one above!</div>
              ) : (
                <table className="ledger-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Amount</th>
                      <th>Who Owes</th>
                      <th>Who Is Owed</th>
                      <th>Status</th>
                      <th>Approvals</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wagers.map(w => (
                      <React.Fragment key={w.id}>
                        <tr onClick={() => setExpandedId(expandedId === `main-${w.id}` ? null : `main-${w.id}`)}
                            style={{ cursor: 'pointer' }}>
                          <td>{new Date(w.created_at).toLocaleDateString()}</td>
                          <td><strong>{w.is_settlement ? '\uD83D\uDCB0 ' : ''}{w.title}</strong></td>
                          <td>{w.is_settlement ? 'Payment' : (w.category_name || '-')}</td>
                          <td>${parseFloat(w.amount).toFixed(2)}</td>
                          <td>{w.owed_by_username}</td>
                          <td>{w.owed_to_username}</td>
                          <td><span className={`status-badge ${w.status}`}>{w.status}</span></td>
                          <td>
                            <div className="approval-list">
                              {w.approvals?.map(a => (
                                <span key={a.id} className={`approval-chip ${a.status}`}>
                                  {a.username}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td onClick={e => e.stopPropagation()}>
                            {w.status === 'pending' && needsMyApproval(w) && (
                              <>
                                <button className="action-btn approve" onClick={() => handleApprove(w.id)}>Approve</button>
                                <button className="action-btn deny" onClick={() => handleDeny(w.id)}>Deny</button>
                              </>
                            )}
                            {w.status === 'approved' && !w.is_settlement && w.owed_by === currentUserId && !hasPendingSettlement(w.owed_by, w.owed_to) && (
                              <button className="action-btn settle" onClick={() => handleMarkPaid(w.id)}>Mark Paid</button>
                            )}
                          </td>
                        </tr>
                        {expandedId === `main-${w.id}` && (
                          <tr className="wager-detail-row">
                            <td colSpan={9}>
                              <div className="wager-detail-content">
                                {w.description && <p><strong>Description:</strong> {w.description}</p>}
                                {w.notes && <p><strong>Notes:</strong> {w.notes}</p>}
                                <p><strong>Submitted by:</strong> {w.submitted_by_username}</p>
                                <p><strong>Participants:</strong> {w.participants?.map(p => p.username).join(', ')}</p>
                                <p><strong>Approval details:</strong></p>
                                {w.approvals?.map(a => (
                                  <p key={a.id} style={{ marginLeft: '15px' }}>
                                    {a.username}: <span className={`status-badge ${a.status}`}>{a.status}</span>
                                    {a.responded_at && ` (${new Date(a.responded_at).toLocaleString()})`}
                                  </p>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Balance Summary */}
            <div className="ledger-section">
              <h2>Balance Summary</h2>
              {balances.pairwise.length === 0 && balances.net.length === 0 ? (
                <div className="empty-state">No balances yet. Approve some wagers to see totals.</div>
              ) : (
                <div className="balance-grid">
                  <div className="balance-card">
                    <h3>Who Owes Whom</h3>
                    {balances.pairwise.map((entry, i) => (
                      <div className="balance-item" key={i}>
                        <span>{entry.from_username} owes {entry.to_username}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className="balance-amount negative">${parseFloat(entry.amount).toFixed(2)}</span>
                          {entry.from_user === currentUserId && !hasPendingSettlement(entry.from_user, entry.to_user) && (
                            <button className="action-btn settle" onClick={async () => {
                              try {
                                await axiosInstance.post('wagers/settle_up/', {
                                  paid_to: entry.to_user,
                                  amount: entry.amount,
                                  notes: 'Full balance settlement',
                                });
                                toast.success(`Payment of $${parseFloat(entry.amount).toFixed(2)} to ${entry.to_username} submitted for confirmation!`);
                                fetchAll();
                              } catch (e) {
                                toast.error(e.response?.data?.error || 'Failed to submit payment');
                              }
                            }}>Pay</button>
                          )}
                          {entry.from_user === currentUserId && hasPendingSettlement(entry.from_user, entry.to_user) && (
                            <span className="status-badge pending" style={{ fontSize: '0.7rem' }}>Payment Pending</span>
                          )}
                        </span>
                      </div>
                    ))}
                    {balances.pairwise.length === 0 && (
                      <div className="empty-state">No outstanding debts.</div>
                    )}
                  </div>
                  <div className="balance-card">
                    <h3>Net Balances</h3>
                    {balances.net.map((entry, i) => {
                      const bal = parseFloat(entry.net_balance);
                      return (
                        <div className="balance-item" key={i}>
                          <span>{entry.username}</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span className={`balance-amount ${bal >= 0 ? 'positive' : 'negative'}`}>
                              {bal >= 0 ? `+$${bal.toFixed(2)} (owed)` : `-$${Math.abs(bal).toFixed(2)} (owes)`}
                            </span>
                            {entry.user_id === currentUserId && bal < 0 && (() => {
                              const myDebts = balances.pairwise.filter(p => p.from_user === currentUserId);
                              const allHavePending = myDebts.every(d => hasPendingSettlement(d.from_user, d.to_user));
                              if (allHavePending) return <span className="status-badge pending" style={{ fontSize: '0.7rem' }}>Payment Pending</span>;
                              return (
                                <button className="action-btn settle" onClick={async () => {
                                  const unpaidDebts = myDebts.filter(d => !hasPendingSettlement(d.from_user, d.to_user));
                                  try {
                                    for (const debt of unpaidDebts) {
                                      await axiosInstance.post('wagers/settle_up/', {
                                        paid_to: debt.to_user,
                                        amount: debt.amount,
                                        notes: 'Full net balance settlement',
                                      });
                                    }
                                    toast.success('All payments submitted for confirmation!');
                                    fetchAll();
                                  } catch (e) {
                                    toast.error(e.response?.data?.error || 'Failed to submit payments');
                                  }
                                }}>Pay All</button>
                              );
                            })()}
                          </span>
                        </div>
                      );
                    })}
                    {balances.net.length === 0 && (
                      <div className="empty-state">No net balances.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </Container>
    </div>
  );
}

export default LedgerPage;
